import { useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";

interface CollapsibleNestedOfferProps {
  /** When set, this offer is a counter and we show collapsible (original) + always-visible (current) content. */
  parentOfferId: string | undefined;
  /** Unique key for this offer (for list key / future use). */
  offerId?: string;
  /** Label for the collapsible header (e.g. "Oferta original"). Only used when nested and collapsibleContent is provided. */
  collapsibleLabel?: string;
  /** Content shown inside the collapsible (e.g. parent offer summary). Only used when nested. */
  collapsibleContent?: React.ReactNode;
  /** Whether the collapsible section is expanded by default when nested. */
  defaultExpanded?: boolean;
  /** Optional extra class for the nesting wrapper (e.g. margin and border). */
  wrapperClassName?: string;
  /** Always visible: the current offer card. */
  children: React.ReactNode;
}

/**
 * For nested (counter) offers: shows the original offer in a collapsible section and
 * always shows the current offer (children). Non-nested offers render only children.
 */
export function CollapsibleNestedOffer({
  parentOfferId,
  collapsibleLabel = "Oferta original",
  collapsibleContent,
  defaultExpanded = true,
  wrapperClassName = "",
  children,
}: CollapsibleNestedOfferProps) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const isNested = !!parentOfferId;

  if (!isNested) {
    return <>{children}</>;
  }

  return (
    <div className={wrapperClassName} data-collapsible-nested="true">
      {collapsibleContent != null && (
        <>
          <button
            type="button"
            onClick={() => setExpanded((e) => !e)}
            className="flex items-center gap-2 w-full text-left py-2.5 px-3 rounded-lg bg-muted/60 hover:bg-muted border border-border transition-colors mb-2"
            aria-expanded={expanded}
          >
            {expanded ? (
              <ChevronDown className="w-4 h-4 text-primary shrink-0" aria-hidden />
            ) : (
              <ChevronRight className="w-4 h-4 text-primary shrink-0" aria-hidden />
            )}
            <span className="text-sm font-medium text-foreground">{collapsibleLabel}</span>
            {!expanded && (
              <span className="text-xs text-muted-foreground ml-1">(clique para expandir)</span>
            )}
          </button>
          {/* Simple show/hide so collapse always works; no grid so no layout quirks */}
          {expanded && (
            <div className="mb-3 overflow-hidden">
              {collapsibleContent}
            </div>
          )}
        </>
      )}
      {/* Current offer is always visible */}
      {children}
    </div>
  );
}
